import {
  createSlice,
  PayloadAction,
  ActionReducerMapBuilder,
} from "@reduxjs/toolkit";
import { RootState } from "../store";
import { fetchNodeSchedule } from "../actions/scheduleActions";

// Types
export interface SessionState {
  data: any; // Simplified - the actual data varies by state type
  entered_at: string;
}

export interface NegotiatedCodecDiagnostic {
  codec: string;
  negotiation_duration_sec: number;
  created_timestamp: string;
}

export interface SessionTelemetry {
  negotiated_codec: NegotiatedCodecDiagnostic | null;
}

export interface APISession {
  session_id: string;
  deployment_id: string;
  organization_id: string;
  build_id?: string | null;
  status: string;
  conversation_id: string | null;
  connection_method: string;
  created_at: string;
  expire_after: string | null;
  internal_user_id: string | null;
  user_email: string | null;
  state_history?: SessionState[] | null;
  log_session_id?: string | null;
  telemetry?: SessionTelemetry | null;
  launch_args_id?: string | null;
}

export interface UnscheduledAPISession {
  s: APISession;
  rationale_per_node: Record<string, string[]>;
}

export interface RichSession {
  s: APISession | null;
  session_id?: string | null;
  build_id?: string | null;
  is_phantom: boolean;
  phantom_deployment_id?: string | null;
  phantom_build_id?: string | null;
  scheduling_status?: "initializing" | "running" | "stopping" | "stopped" | "error" | null;
  launch_args?: string[] | null;
  launch_time?: string | null;
}

export interface NodeSchedule {
  node_id: string;
  node_type: string;
  node_status: string;
  node_idle_s: number | null;
  available_build_ids: string[] | null;
  sessions: RichSession[];
}

export interface InspectNodeScheduleResponse {
  node_schedules: NodeSchedule[];
  unscheduled_sessions: UnscheduledAPISession[];
  nearly_schedulable_sessions: APISession[];
  schedule_created_at_sec: number;
}

export interface ScheduleState {
  snapshot: InspectNodeScheduleResponse | null; // what user currently sees
  latest: InspectNodeScheduleResponse | null;   // fresh data (hidden until refresh)
  loading: boolean;
  error: string | null;
  hasNewData: boolean; // true when latest != snapshot
}

// Helper functions
const IGNORED_SESSION_KEYS = new Set(["created_at", "expire_after"]);
const IGNORED_UNSCHEDULED_SESSION_KEYS = new Set(["rationale_per_node"]);
const IGNORED_NODE_KEYS = new Set(["node_idle_s"]);

export function isScheduleEqual(
  a: InspectNodeScheduleResponse,
  b: InspectNodeScheduleResponse
): boolean {
  const filteredA = sanitizeSchedule(a);
  const filteredB = sanitizeSchedule(b);
  return JSON.stringify(filteredA) === JSON.stringify(filteredB);
}

function sanitizeSchedule(
  schedule: InspectNodeScheduleResponse
): InspectNodeScheduleResponse {
  return {
    ...schedule,
    schedule_created_at_sec: 0, // ignore timestamp
    node_schedules: schedule.node_schedules.map((node) => ({
      ...omitKeys(node, IGNORED_NODE_KEYS),
      sessions: node.sessions.map((s) => ({
        ...s,
        s: s.s ? omitKeys(s.s, IGNORED_SESSION_KEYS) : null,
      })),
    })),
    unscheduled_sessions: schedule.unscheduled_sessions.map((unscheduled) => ({
      ...omitKeys(unscheduled, IGNORED_UNSCHEDULED_SESSION_KEYS),
      s: omitKeys(unscheduled.s, IGNORED_SESSION_KEYS),
    })),
    nearly_schedulable_sessions: schedule.nearly_schedulable_sessions?.map((session) => 
      omitKeys(session, IGNORED_SESSION_KEYS)
    ) || [],
  };
}

function omitKeys<T extends object>(obj: T, keys: Set<string>): T {
  const result = {} as T;
  Object.entries(obj).forEach(([k, v]) => {
    if (!keys.has(k)) {
      (result as any)[k] = v;
    }
  });
  return result;
}

// Initial state
const initialState: ScheduleState = {
  snapshot: null,
  latest: null,
  loading: false,
  error: null,
  hasNewData: false,
};

// Slice
const scheduleSlice = createSlice({
  name: "schedule",
  initialState,
  reducers: {
    acceptLatest(state) {
      state.snapshot = state.latest;
      state.hasNewData = false;
    },
  },
  extraReducers: (builder: ActionReducerMapBuilder<ScheduleState>) => {
    builder
      .addCase(fetchNodeSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchNodeSchedule.fulfilled,
        (state, action: PayloadAction<InspectNodeScheduleResponse>) => {
          state.loading = false;
          const incoming = action.payload;

          if (!state.snapshot) {
            // First load → snapshot & latest are the same
            state.snapshot = incoming;
            state.latest = incoming;
            state.hasNewData = false;
          } else {
            state.latest = incoming;
            // Decide if there is a meaningful diff
            state.hasNewData = !isScheduleEqual(state.snapshot, incoming);
          }
        }
      )
      .addCase(fetchNodeSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch node schedule";
      });
  },
});

export const { acceptLatest } = scheduleSlice.actions;
export default scheduleSlice.reducer;

// Selectors
export const selectSnapshot = (state: RootState) => state.schedule!.snapshot;
export const selectHasNewData = (state: RootState) => state.schedule!.hasNewData;
export const selectLoading = (state: RootState) => state.schedule!.loading;
export const selectError = (state: RootState) => state.schedule!.error;
